import { useEffect, useState } from 'react'
import {
  ApiCreateRespType,
  FormType,
  NormalFormData,
  ParamsType,
  ProfessionType,
  SessionType,
} from '../utils/sharedTypes'
import { FormDefaultState } from '../utils/constants'
import { client } from '../utils/client'
import Bugsnag from '@bugsnag/js'
import { Navigate, Route, Routes } from 'react-router-dom'
import {
  Alma,
  Financement,
  Information,
  Recap,
  Recommandation,
  DPCRegistration,
  PreInscription,
  Stripe,
  BankTransfer,
  Signature,
  DocusignSuccess,
  TallyForm,
  Positionnement,
  OpcoValidation,
  CPFRedirect,
  CPFSignature,
} from '../pages'

export default function Registration() {
  const [params, setParams] = useState<ParamsType>({})
  const [form, setForm] = useState<FormType>(FormDefaultState)

  useEffect(() => {
    if (form.data.global_id && !params.global_id) {
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set('global_id', form.data.global_id)
      const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString()
      window.history.replaceState(null, '', newRelativePathQuery)
      setParams((prev) => ({ ...prev, global_id: form.data.global_id }))
    }
  }, [form.data.global_id, params.global_id])

  useEffect(() => {
    if (params.global_id) {
      client
        .get(`/registration/${params.global_id}/`)
        .then((resp) => {
          const data = resp.data as NormalFormData
          setForm((prevState) => {
            return {
              ...prevState,
              data: {
                ...prevState.data,
                ...data,
                alma_installments_count:
                  data.alma_installments_count || (data.personal_funding ? 1 : data.alma_installments_count),
              },
            }
          })
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
    }
  }, [params.global_id])

  useEffect(() => {
    if (params.product_code) {
      //INIT INSCRIPTION
      const payload: {
        entrypoint_url: string
        product: { product_code: string }
        registration_group?: { global_id: string }
      } = {
        entrypoint_url: decodeURIComponent(params.entrypoint_url ?? ''),
        product: {
          product_code: params.product_code.toLowerCase(),
        },
      }
      if (params.registration_group) {
        payload.registration_group = { global_id: params.registration_group }
      }
      client
        .post('/registration/', payload)
        .then((resp) => {
          const data = resp.data as ApiCreateRespType
          setForm((prevState) => {
            return {
              ...prevState,
              data: {
                ...prevState.data,
                global_id: data.global_id,
                product: data.product,
                theme: data.theme,
                registration_group: data.registration_group ?? prevState.data.registration_group,
              },
            }
          })
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })

      //GET PROFESSIONS
      client
        .get(`/registration/content/professions/?product=${params.product_code.toLowerCase()}`)
        .then((resp) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const data = resp.data.results as ProfessionType[]
          let reorderedData = data
          const indexOfOther = data?.findIndex((p) => p.code === 'unknown')
          if (indexOfOther !== -1) {
            reorderedData = [...data.slice(0, indexOfOther), ...data.slice(indexOfOther + 1), data[indexOfOther]]
          }

          setForm((prevState) => {
            return {
              ...prevState,
              professions: reorderedData,
            }
          })
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
    }
  }, [params])

  useEffect(() => {
    const productCode = form.data.product?.product_code || form.data.products[0]?.product_code
    if (
      productCode &&
      form.data.funding_organism?.code &&
      form.data.profession?.code &&
      form.data.funding_organism.code === 'FIFPL'
    ) {
      client
        .get(
          `/registration/content/sessions/?product_code=${productCode.toLowerCase()}&funding_organism=${form.data.funding_organism.code.toLowerCase()}&profession=${form.data.profession.code.toLowerCase()}`,
        )
        .then((resp) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const data = resp.data.results as SessionType[]
          setForm((prevState) => {
            return {
              ...prevState,
              data: {
                ...prevState.data,
                session:
                  prevState.data.session?.global_id || data.length === 0
                    ? prevState.data.session
                    : { global_id: data[0].global_id },
              },
              sessions: data,
            }
          })
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
    }
  }, [form.data.product, form.data.products, form.data.funding_organism?.code, form?.data?.profession?.code])

  useEffect(() => {
    const fundingCode = form.data.funding_organism?.code
    const hasCustomProcesses = form.data.custom_processes_validation
    const hasDocusign = Boolean(
      form.data?.pre_registration_custom_processes?.find(
        (cp) => cp.active && cp.sales_process.sf_value?.startsWith('docusign_'),
      ),
    )

    let steps: Array<{ label: string; url: string }>

    if (fundingCode === 'CPF') {
      // CPF: Information → [Questionnaire] → cpf
      steps = [
        { label: 'Informations personnelles', url: 'information' },
        { label: 'Récapitulatif', url: 'cpf' },
      ]
      if (hasCustomProcesses) {
        steps.splice(1, 0, { label: 'Questionnaire', url: 'questionnaire' })
      }
      if (form.data.cpf_documents_signature?.required) {
        const recapIndex = steps.findIndex((s) => s.url === 'cpf')
        if (recapIndex !== -1) {
          steps.splice(recapIndex, 0, { label: 'Signature', url: 'cpf-signature' })
        }
      }
    } else if (fundingCode === 'DPC' || fundingCode === 'FIFPL' || fundingCode === 'FAFPM') {
      // DPC/FIFPL/FAFPM: Financement d'abord, puis Questionnaire si besoin
      steps = [
        { label: 'Informations personnelles', url: 'information' },
        { label: 'Financement', url: 'financement' },
        { label: 'Récapitulatif', url: 'recapitulatif' },
      ]
      if (hasCustomProcesses) {
        steps.splice(2, 0, { label: 'Questionnaire', url: 'questionnaire' })
      }
      if (hasCustomProcesses && hasDocusign) {
        const recapIndex = steps.findIndex((s) => s.url === 'recapitulatif')
        if (recapIndex !== -1) {
          steps.splice(recapIndex, 0, { label: 'Signature', url: 'signature' })
        }
      }
    } else {
      // Reste (OPCO, ALMA, STRIPE, etc.) ou financement non choisi : Questionnaire avant Financement si besoin
      steps = [
        { label: 'Informations personnelles', url: 'information' },
        { label: 'Financement', url: 'financement' },
        { label: 'Récapitulatif', url: 'recapitulatif' },
      ]
      if (hasCustomProcesses) {
        steps.splice(1, 0, { label: 'Questionnaire', url: 'questionnaire' })
      }
      if (hasCustomProcesses && hasDocusign) {
        const recapIndex = steps.findIndex((s) => s.url === 'recapitulatif')
        if (recapIndex !== -1) {
          steps.splice(recapIndex, 0, { label: 'Signature', url: 'signature' })
        }
      }
    }

    if (form.data.positioning_status?.required) {
      const anchor = hasCustomProcesses ? 'questionnaire' : 'information'
      const anchorIndex = steps.findIndex((s) => s.url === anchor)
      if (anchorIndex !== -1 && !steps.some((s) => s.url === 'positionnement')) {
        steps.splice(anchorIndex + 1, 0, { label: 'Positionnement', url: 'positionnement' })
      }
    }

    setForm((prevState) => ({
      ...prevState,
      data: { ...prevState.data },
      steps,
    }))
  }, [
    form.data.funding_organism,
    form.data.custom_processes_validation,
    form.data.positioning_status,
    form.data.cpf_documents_signature,
  ])

  useEffect(() => {
    const searchObj = window.location.search ? Object.fromEntries(new URLSearchParams(window.location.search)) : {}
    setParams(searchObj)
    setForm((prevState) => {
      return {
        ...prevState,
        data: {
          ...prevState.data,
          utm_campaign: searchObj.utm_campaign || null,
          utm_source: searchObj.utm_source || null,
          utm_medium: searchObj.utm_medium || null,
          utm_content: searchObj.utm_content || null,
          utm_term: searchObj.utm_term || null,
          adset_id: searchObj.adset_id || null,
          entrypoint_url: decodeURIComponent(searchObj.entrypoint_url) || null,
        },
      }
    })
  }, [])

  useEffect(() => {
    console.log('Steps have updated (Registration.tsx)', form.steps)
  }, [form.steps])

  return (
    <Routes>
      <Route path="/information" element={<Information form={form} setForm={setForm} />} />
      <Route path="/financement" element={<Financement form={form} setForm={setForm} />} />
      <Route path="/pre-inscription" element={<PreInscription form={form} setForm={setForm} />} />
      <Route path="/signature" element={<Signature form={form} setForm={setForm} />} />
      <Route path="/recapitulatif" element={<Recap form={form} setForm={setForm} />} />
      <Route path="/questionnaire" element={<TallyForm form={form} setForm={setForm} />} />
      <Route path="/positionnement" element={<Positionnement form={form} setForm={setForm} />} />
      <Route path="/cpf-signature" element={<CPFSignature form={form} setForm={setForm} />} />
      <Route path="/cpf" element={<CPFRedirect form={form} setForm={setForm} />} />
      <Route path="/alma" element={<Alma form={form} />} />
      <Route path="/stripe" element={<Stripe form={form} />} />
      <Route path="/recommandation" element={<Recommandation form={form} />} />
      <Route path="/virement" element={<BankTransfer form={form} />} />
      <Route path="inscription_dpc" element={<DPCRegistration form={form} setForm={setForm} />} />
      <Route path="/opco/validation" element={<OpcoValidation form={form} />} />
      <Route path="/docusign_success" element={<DocusignSuccess />} />
      <Route path="*" element={<Navigate to="information" replace />} />
    </Routes>
  )
}
